import React, { useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { LuHistory, LuZap } from 'react-icons/lu';
import Page from '../../components/ui/Page/Page';
import PageHeader from '../../components/ui/PageHeader/PageHeader';
import Card from '../../components/ui/Card/Card';
import Button from '../../components/ui/Button/Button';
import EmptyState from '../../components/ui/EmptyState/EmptyState';
import ErrorState from '../../components/ui/ErrorState/ErrorState';
import { SkeletonCard } from '../../components/ui/LoadingSkeleton/LoadingSkeleton';
import Pagination from '../charge/components/Pagination.jsx';
import HistorySessionCard from './components/HistorySessionCard.jsx';
import { useAccountSessions } from '../../hooks/useAccountSessions';
import './HistoryPage.less';

const PAGE_SIZE = 10;

/**
 * Authenticated charging-history page. `/history` is behind
 * `<ProtectedRoute>` (see `AppRoutes.jsx`) so an unauthenticated user
 * is bounced out before this component ever mounts. Data source is the
 * account API — never localStorage / sessionStorage / device-scoped
 * stores.
 *
 * Server-side pagination against `GET /api/user/sessions?limit&offset`;
 * see `useAccountSessions`. Client-side date filters are intentionally
 * NOT provided here because the current API contract does not accept a
 * date filter — mixing server pagination with client-side filtering
 * would silently omit rows on later pages.
 */
function HistoryPage() {
  const {
    sessions,
    total,
    page,
    pageSize,
    totalPages,
    loading,
    error,
    refresh,
    setPage,
  } = useAccountSessions({ pageSize: PAGE_SIZE });

  // Anchor for the results list — the pagination handler focuses it
  // + scrolls into view so keyboard users land at the top of the new
  // page instead of being stranded next to the Pagination buttons.
  const resultsHeadingRef = useRef(null);

  const handlePageChange = useCallback(
    (next) => {
      // The hook's own load() ignores redundant setPage() calls, so
      // spam-clicking Next is safe; the sequence guard also drops
      // stale responses.
      if (loading) return;
      setPage(next);
      requestAnimationFrame(() => {
        const el = resultsHeadingRef.current;
        if (!el) return;
        el.focus({ preventScroll: false });
        if (typeof el.scrollIntoView === 'function') {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    },
    [loading, setPage],
  );

  const isInitialLoad = loading && sessions.length === 0 && !error;
  const isPageChange = loading && sessions.length > 0;

  // Only surface pagination + toolbar once we have a real response.
  const hasResult = !isInitialLoad && !error && total > 0;
  const rangeStart = hasResult ? (page - 1) * pageSize + 1 : 0;
  const rangeEnd = hasResult
    ? Math.min(page * pageSize, total)
    : 0;

  return (
    <Page>
      <PageHeader title="Charging history" subtitle="Your charging sessions." />

      {/* Error state — retryable, doesn't wipe the previous page. */}
      {!isInitialLoad && error && (
        <Card>
          <ErrorState
            title="We couldn't load your charging history"
            description="Please check your connection and try again."
            onRetry={refresh}
          />
        </Card>
      )}

      {/* Initial-load skeleton — page shell already present via header. */}
      {isInitialLoad && (
        <div className="gc-history-list" aria-busy="true" aria-live="polite">
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
        </div>
      )}

      {/* True empty state — only when server confirmed zero sessions. */}
      {!isInitialLoad && !error && total === 0 && (
        <Card>
          <EmptyState
            icon={<LuHistory />}
            title="No charging sessions yet"
            description="Once you complete your first charging session, it will appear here."
            action={
              <Button as={Link} to="/charge" leadingIcon={<LuZap />}>
                Find a charger
              </Button>
            }
          />
        </Card>
      )}

      {/* Results — compact rows + toolbar + pagination. */}
      {hasResult && (
        <section
          className="gc-history-section"
          aria-labelledby="gc-history-heading"
        >
          <div className="gc-history-toolbar">
            <h2
              id="gc-history-heading"
              ref={resultsHeadingRef}
              tabIndex={-1}
              className="gc-history-toolbar-title"
            >
              {total === 1
                ? '1 session'
                : `${total} sessions`}
            </h2>
            <span className="gc-history-toolbar-range" aria-live="polite">
              Showing {rangeStart}–{rangeEnd} of {total}
            </span>
          </div>

          {/* Page-change skeleton lives INSIDE the section so the
              toolbar + pagination stay put (no page-level shell flash). */}
          {isPageChange && (
            <div
              className="gc-history-list gc-history-list--refreshing"
              aria-busy="true"
              aria-live="polite"
            >
              <SkeletonCard />
              <SkeletonCard />
              <SkeletonCard />
            </div>
          )}

          {!isPageChange && (
            <div className="gc-history-list">
              {sessions.map((s) => (
                <HistorySessionCard
                  key={s.sessionUid}
                  session={s}
                  to={`/history/${encodeURIComponent(s.sessionUid)}`}
                />
              ))}
            </div>
          )}

          <Pagination
            page={page}
            totalPages={totalPages}
            onChange={handlePageChange}
            ariaLabel="Charging history pagination"
          />
        </section>
      )}
    </Page>
  );
}

export default HistoryPage;
